/**
 * What the Focus Group picker can open: chat segments and, since spec 89, whole chat audiences. The two id
 * spaces overlap, so every picker value carries its kind — `s:5`, `a:1` — never a bare id.
 */
import { DEFAULT_STARTER_QUESTIONS, starterQuestionsFor } from './starterQuestions';

const SESSIONS_PATH = '/healthcare-chat-v2/sessions';

/** `segments[]` of `GET /healthcare-chat-v2/segments`; empty `starter_questions` = no usable subject. */
export interface ServerSegment {
  id: number;
  name: string;
  admin_only?: boolean;
  starter_questions?: string[];
}

/** `audiences[]` of the same endpoint (spec 89); an older backend omits the key. */
export interface ServerAudience {
  id: number;
  name: string;
  segment_count?: number;
  openable: boolean;
  why_not?: string | null;
  starter_questions?: string[];
}

export type ChatTargetKind = 'segment' | 'audience';

export interface ChatTarget {
  key: string;
  kind: ChatTargetKind;
  id: number;
  name: string;
  /** A non-openable audience: listed so the client sees it, never selectable. */
  disabled: boolean;
  hint?: string;
}

const KIND_PREFIX: Record<ChatTargetKind, string> = { segment: 's', audience: 'a' };
const KEY_SEPARATOR = ':';

export const chatTargetKey = (kind: ChatTargetKind, id: number): string =>
  `${KIND_PREFIX[kind]}${KEY_SEPARATOR}${id}`;

export function parseChatTargetKey(
  key: string | null,
): { kind: ChatTargetKind; id: number } | null {
  if (!key) return null;
  const [prefix, raw] = key.split(KEY_SEPARATOR);
  const kind = (Object.keys(KIND_PREFIX) as ChatTargetKind[]).find(
    (k) => KIND_PREFIX[k] === prefix,
  );
  const id = Number(raw);
  return kind && Number.isInteger(id) ? { kind, id } : null;
}

export const segmentTarget = (id: number, name: string): ChatTarget => ({
  key: chatTargetKey('segment', id),
  kind: 'segment',
  id,
  name,
  disabled: false,
});

const audienceTarget = (a: ServerAudience): ChatTarget => ({
  key: chatTargetKey('audience', a.id),
  kind: 'audience',
  id: a.id,
  name: a.name,
  disabled: !a.openable,
  hint: a.openable ? undefined : (a.why_not ?? undefined),
});

const NAME_ORDER = new Intl.Collator('en', { sensitivity: 'base' });

/** One list by name, ignoring case. With no audience it is exactly today's: the server's order. */
export function serverChatTargets(
  segments: ServerSegment[],
  audiences: ServerAudience[],
): ChatTarget[] {
  const targets = segments.map((s) => segmentTarget(s.id, s.name));
  if (audiences.length === 0) return targets;
  return [...targets, ...audiences.map(audienceTarget)].sort((a, b) =>
    NAME_ORDER.compare(a.name, b.name),
  );
}

/** Reads the picker response. An empty list is an answer; null means the body held no list at all. */
export function readPickerResponse(
  data: unknown,
): { segments: ServerSegment[]; audiences: ServerAudience[] } | null {
  const body = (data ?? {}) as { segments?: unknown; audiences?: unknown };
  if (!Array.isArray(body.segments) && !Array.isArray(body.audiences)) return null;
  const segments = Array.isArray(body.segments) ? (body.segments as ServerSegment[]) : [];
  const audiences = Array.isArray(body.audiences) ? (body.audiences as ServerAudience[]) : [];
  return { segments, audiences };
}

/** The body `POST /healthcare-chat-v2/sessions` takes for this target. */
export const sessionCreateBody = (target: { kind: ChatTargetKind; id: number }) =>
  target.kind === 'audience' ? { audience_id: target.id } : { segment_id: target.id };

/** The history filter for this target; a segment's list excludes audience chats server-side. */
export const sessionsQuery = (target: { kind: ChatTargetKind; id: number }): string =>
  target.kind === 'audience' ? `audience_id=${target.id}` : `segment_id=${target.id}`;

/** The picker key of the audience a session belongs to, from its row in the unfiltered session list. */
export async function audienceKeyOfSession(
  apiUrl: string,
  sessionId: number,
): Promise<string | null> {
  try {
    const res = await fetch(`${apiUrl}${SESSIONS_PATH}`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = (await res.json()) as { sessions?: { id: number; audience_id?: number | null }[] };
    const row = (data.sessions ?? []).find((s) => s.id === sessionId);
    return row?.audience_id != null ? chatTargetKey('audience', row.audience_id) : null;
  } catch {
    return null;
  }
}

/**
 * Prompt chips for a target. The curated map is keyed by SEGMENT id, so an audience never consults it —
 * audience 955 is not the Natural Organic segment 955.
 */
export const starterQuestionsForTarget = (
  target: { kind: ChatTargetKind; id: number },
  fromServer?: readonly string[],
): readonly string[] => {
  if (target.kind === 'segment') return starterQuestionsFor(target.id, fromServer);
  return fromServer && fromServer.length > 0 ? fromServer : DEFAULT_STARTER_QUESTIONS;
};
